import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

// Detect mobile for performance optimization
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

const PARTICLE_COUNT = isMobile ? 2500 : 8000
const FOREGROUND_COUNT = isMobile ? 30 : 200

export default function Galaxy({ mouse }) {
    const points = useRef()
    const foregroundPoints = useRef()
    const time = useRef(0)

    // Generate main galaxy particles with flow data
    const [positions, colors, sizes, originalData] = useMemo(() => {
        const positions = new Float32Array(PARTICLE_COUNT * 3)
        const colors = new Float32Array(PARTICLE_COUNT * 3)
        const sizes = new Float32Array(PARTICLE_COUNT)
        const originalData = new Float32Array(PARTICLE_COUNT * 3)

        const colorPalette = [
            new THREE.Color('#f97316'),
            new THREE.Color('#fbbf24'),
            new THREE.Color('#00f3ff'),
            new THREE.Color('#06b6d4'),
        ]

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3
            const radius = Math.random() * 15 + 0.5
            const spinAngle = radius * 0.5
            const branchAngle = (i % 3) * ((Math.PI * 2) / 3)

            originalData[i3] = radius
            originalData[i3 + 1] = branchAngle + spinAngle
            originalData[i3 + 2] = (Math.random() - 0.5) * 1.5

            const randomX = (Math.random() - 0.5) * 2 * Math.pow(radius / 15, 0.5)
            const randomY = (Math.random() - 0.5) * 1.5 * Math.pow(radius / 15, 0.5)
            const randomZ = (Math.random() - 0.5) * 2 * Math.pow(radius / 15, 0.5)

            positions[i3] = Math.cos(branchAngle + spinAngle) * radius + randomX
            positions[i3 + 1] = randomY
            positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * radius + randomZ

            const colorMix = Math.min(radius / 12, 1)
            const color = colorPalette[Math.floor(colorMix * (colorPalette.length - 1))]
            const mixColor = color.clone()
            mixColor.lerp(colorPalette[Math.floor(Math.random() * colorPalette.length)], 0.3)

            colors[i3] = mixColor.r
            colors[i3 + 1] = mixColor.g
            colors[i3 + 2] = mixColor.b

            // Slightly larger on mobile for visibility
            sizes[i] = (1 - colorMix * 0.5) * (0.5 + Math.random() * 0.5) * (isMobile ? 1.3 : 1)
        }

        return [positions, colors, sizes, originalData]
    }, [])

    // Generate foreground particles that pass close to camera
    const [fgPositions, fgColors, fgSizes, fgData] = useMemo(() => {
        const positions = new Float32Array(FOREGROUND_COUNT * 3)
        const colors = new Float32Array(FOREGROUND_COUNT * 3)
        const sizes = new Float32Array(FOREGROUND_COUNT)
        const data = new Float32Array(FOREGROUND_COUNT * 4)

        const colorPalette = [
            new THREE.Color('#00f3ff'),
            new THREE.Color('#06b6d4'),
            new THREE.Color('#fbbf24'),
        ]

        for (let i = 0; i < FOREGROUND_COUNT; i++) {
            const i3 = i * 3
            const i4 = i * 4

            positions[i3] = (Math.random() - 0.5) * 30
            positions[i3 + 1] = (Math.random() - 0.5) * 20
            positions[i3 + 2] = Math.random() * 8 + 4

            data[i4] = positions[i3]
            data[i4 + 1] = positions[i3 + 1]
            data[i4 + 2] = positions[i3 + 2]
            data[i4 + 3] = 0.2 + Math.random() * 0.5

            const color = colorPalette[Math.floor(Math.random() * colorPalette.length)]
            colors[i3] = color.r
            colors[i3 + 1] = color.g
            colors[i3 + 2] = color.b

            sizes[i] = (0.8 + Math.random() * 1.5) * (isMobile ? 1.5 : 1)
        }

        return [positions, colors, sizes, data]
    }, [])

    // Animate all particles
    useFrame((state, delta) => {
        if (!points.current) return

        time.current += delta

        const posAttr = points.current.geometry.attributes.position
        const colorAttr = points.current.geometry.attributes.color

        const colorPalette = [
            new THREE.Color('#f97316'),
            new THREE.Color('#fbbf24'),
            new THREE.Color('#00f3ff'),
            new THREE.Color('#06b6d4'),
        ]

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3
            let radius = originalData[i3]
            const angle = originalData[i3 + 1]
            const yOffset = originalData[i3 + 2]

            const flowSpeed = 0.008
            const maxRadius = 15.5
            const minRadius = 0.3

            radius = radius - delta * flowSpeed
            if (radius < minRadius) radius = maxRadius
            originalData[i3] = radius

            const rotationSpeed = 0.03 / (radius * 0.3 + 0.5)
            const currentAngle = angle + time.current * rotationSpeed

            const scatter = Math.pow(radius / 15, 0.5)
            const randomX = Math.sin(i * 12.345 + time.current * 0.05) * scatter * 0.5
            const randomZ = Math.cos(i * 23.456 + time.current * 0.05) * scatter * 0.5

            posAttr.array[i3] = Math.cos(currentAngle) * radius + randomX
            posAttr.array[i3 + 1] = yOffset * scatter
            posAttr.array[i3 + 2] = Math.sin(currentAngle) * radius + randomZ

            const colorMix = Math.min(radius / 12, 1)
            const colorIndex = Math.floor(colorMix * (colorPalette.length - 1))
            const nextColorIndex = Math.min(colorIndex + 1, colorPalette.length - 1)
            const colorT = (colorMix * (colorPalette.length - 1)) % 1
            const color = colorPalette[colorIndex].clone().lerp(colorPalette[nextColorIndex], colorT)

            colorAttr.array[i3] = color.r
            colorAttr.array[i3 + 1] = color.g
            colorAttr.array[i3 + 2] = color.b
        }

        posAttr.needsUpdate = true
        colorAttr.needsUpdate = true

        // Foreground particles
        if (foregroundPoints.current) {
            const fgPosAttr = foregroundPoints.current.geometry.attributes.position

            for (let i = 0; i < FOREGROUND_COUNT; i++) {
                const i3 = i * 3
                const i4 = i * 4

                const speed = fgData[i4 + 3]
                const baseX = fgData[i4]
                const baseY = fgData[i4 + 1]
                const phase = i * 0.5

                fgPosAttr.array[i3] = baseX + Math.sin(time.current * speed * 0.08 + phase) * 3
                fgPosAttr.array[i3 + 1] = baseY + Math.cos(time.current * speed * 0.05 + phase) * 2
                fgPosAttr.array[i3 + 2] -= delta * speed * 0.1

                if (fgPosAttr.array[i3 + 2] < 2) {
                    fgPosAttr.array[i3 + 2] = 15
                    fgData[i4] = (Math.random() - 0.5) * 30
                    fgData[i4 + 1] = (Math.random() - 0.5) * 20
                }
            }

            fgPosAttr.needsUpdate = true
        }

        // Mouse/touch influence (skip on mobile for performance)
        if (mouse && !isMobile) {
            const targetX = mouse.x * 0.3
            const targetY = mouse.y * 0.2
            points.current.rotation.x += (targetY - points.current.rotation.x) * 0.02
            points.current.position.x += (targetX * 2 - points.current.position.x) * 0.01
        }
    })

    return (
        <group>
            <points ref={points}>
                <bufferGeometry>
                    <bufferAttribute attach="attributes-position" count={PARTICLE_COUNT} array={positions} itemSize={3} />
                    <bufferAttribute attach="attributes-color" count={PARTICLE_COUNT} array={colors} itemSize={3} />
                    <bufferAttribute attach="attributes-size" count={PARTICLE_COUNT} array={sizes} itemSize={1} />
                </bufferGeometry>
                <shaderMaterial
                    transparent
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                    vertexColors
                    vertexShader={`
                        attribute float size;
                        varying vec3 vColor;
                        void main() {
                            vColor = color;
                            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                            gl_PointSize = size * (200.0 / -mvPosition.z);
                            gl_Position = projectionMatrix * mvPosition;
                        }
                    `}
                    fragmentShader={`
                        varying vec3 vColor;
                        void main() {
                            float dist = length(gl_PointCoord - vec2(0.5));
                            if (dist > 0.5) discard;
                            float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
                            alpha *= 0.8;
                            vec3 glow = vColor * (1.0 + (1.0 - dist * 2.0) * 0.5);
                            gl_FragColor = vec4(glow, alpha);
                        }
                    `}
                />
            </points>

            <points ref={foregroundPoints}>
                <bufferGeometry>
                    <bufferAttribute attach="attributes-position" count={FOREGROUND_COUNT} array={fgPositions} itemSize={3} />
                    <bufferAttribute attach="attributes-color" count={FOREGROUND_COUNT} array={fgColors} itemSize={3} />
                    <bufferAttribute attach="attributes-size" count={FOREGROUND_COUNT} array={fgSizes} itemSize={1} />
                </bufferGeometry>
                <shaderMaterial
                    transparent
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                    vertexColors
                    vertexShader={`
                        attribute float size;
                        varying vec3 vColor;
                        void main() {
                            vColor = color;
                            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                            gl_PointSize = size * (300.0 / -mvPosition.z);
                            gl_Position = projectionMatrix * mvPosition;
                        }
                    `}
                    fragmentShader={`
                        varying vec3 vColor;
                        void main() {
                            float dist = length(gl_PointCoord - vec2(0.5));
                            if (dist > 0.5) discard;
                            float alpha = 1.0 - smoothstep(0.1, 0.5, dist);
                            alpha *= 0.4;
                            vec3 glow = vColor * (1.0 + (1.0 - dist * 2.0) * 0.8);
                            gl_FragColor = vec4(glow, alpha);
                        }
                    `}
                />
            </points>
        </group>
    )
}
